import {
  deserializeYesplanAPIDateAndTime,
  deserializeYesplanAPITime,
  isOnSameDate,
  timeDifferenceInHours
} from '../libs/dateTimeUtilities';
import {
  printInHourDisplayFormat,
  printInShortDateAndHourFormat
} from '../libs/dateTimePrinting';
import YPAbstractEventObject from './YPAbstractEventObject';

export default class YPEventObject extends YPAbstractEventObject {
  constructor(eventsJSON) {
    super(eventsJSON);
    this.defaultschedulestart = eventsJSON.defaultschedulestart
      ? deserializeYesplanAPIDateAndTime(eventsJSON.defaultschedulestart)
      : null;
    this.defaultscheduleend = eventsJSON.defaultscheduleend
      ? deserializeYesplanAPIDateAndTime(eventsJSON.defaultscheduleend)
      : null;
    this.defaultschedulestarttime = eventsJSON.defaultschedulestarttime
      ? deserializeYesplanAPITime(eventsJSON.defaultschedulestarttime)
      : null;
    this.defaultscheduleendtime = eventsJSON.defaultscheduleendtime
      ? deserializeYesplanAPITime(eventsJSON.defaultscheduleendtime)
      : null;
  }

  /* Date will only be displayed if it differs from the event's start date */
  #getEventScheduleDateAndTimeObject(
    eventDateToCompareWith,
    scheduleDateAndTime,
    scheduleTimeObject,
    hideDate = false
  ) {
    /* Construct a date object with the eventdate and the schedule time */
    if (scheduleDateAndTime && scheduleTimeObject) {
      if (!isOnSameDate(eventDateToCompareWith, scheduleDateAndTime) && !hideDate)
        return { includeDate: true, dateObject: scheduleDateAndTime };
      return { includeDate: false, dateObject: scheduleDateAndTime };
    }
    if (scheduleTimeObject) return { includeDate: false, dateObject: scheduleTimeObject };
    return {
      dateObject: null
    };
  }

  getStartTime() {
    return this.defaultschedulestart !== null
      ? this.defaultschedulestart
      : this.starttime;
  }

  eventNameOrPlaceholder() {
    return this.name || `(${[i18n('unnamed event')]})`;
  }

  isGroup() {
    return false;
  }

  equal(anEvent) {
    return anEvent.id === this.id;
  }

  printDateAndTimeString(
    showEventDateString = false,
    timeFormatSetting,
    dateFormatSetting
  ) {
    let timeBetweenStartEndSchedule = false;

    if (this.defaultscheduleendtime && this.endDate && this.defaultscheduleend) {
      timeBetweenStartEndSchedule = timeDifferenceInHours(
        this.defaultscheduleend,
        this.defaultschedulestart
      );
    }

    const { includeDate: includeDateStartString, dateObject: startDateObject } =
      this.#getEventScheduleDateAndTimeObject(
        this.startDate,
        this.defaultschedulestart,
        this.defaultschedulestarttime,
        isOnSameDate(this.defaultschedulestart, this.startdate)
      );

    /* If the scheduled start time falls before the event start time,
     * we need to compare the scheduled end time with the scheduled start
     * time (when determining if it's more than 24h apart and we have to display the date with it)
     * to make sure it's clear on which day the event ends */
    const dateTimeToCompareEndWith = isOnSameDate(
      this.defaultschedulestart,
      this.startDate
    )
      ? this.startDate
      : this.defaultschedulestart;

    /* Show date of end time when it differs with more than 24hours from start time */
    const hideEndDate = timeBetweenStartEndSchedule && timeBetweenStartEndSchedule < 24;
    const { includeDate: includeDateEndString, dateObject: endDateObject } =
      this.#getEventScheduleDateAndTimeObject(
        dateTimeToCompareEndWith,
        this.defaultscheduleend,
        this.defaultscheduleendtime,
        hideEndDate
      );

    if (startDateObject === null && endDateObject === null) {
      if (showEventDateString) return this.printDateString(dateFormatSetting);
      return false;
    }

    const startString = includeDateStartString
      ? printInShortDateAndHourFormat(
          startDateObject,
          timeFormatSetting,
          dateFormatSetting,
          false,
          false
        )
      : printInHourDisplayFormat(startDateObject, timeFormatSetting);
    const endString = includeDateEndString
      ? printInShortDateAndHourFormat(
          endDateObject,
          timeFormatSetting,
          dateFormatSetting,
          false,
          false
        )
      : printInHourDisplayFormat(endDateObject, timeFormatSetting);

    if (startDateObject && endDateObject) {
      const startAndEndTimeString = `${startString}\u{0200A}\u{02013}\u{0200A}${endString}`;
      if (showEventDateString) {
        return `${this.printDateString(dateFormatSetting)} (${startAndEndTimeString})`;
      }
      return `${startAndEndTimeString}`;
    }

    if (startDateObject && !endDateObject) {
      if (showEventDateString) {
        return `${this.printDateString(dateFormatSetting)} (${startString})`;
      }
      return `${startString}`;
    }
    return false;
  }
}
